/**
 * Read-only weekly rota fold-out (Ed, 2026-09-08 — ADR-0040's unnamed
 * phase 0): the client hero's calendar button folds out a Mon–Sun view of
 * the client's `_encounter` shifts. STRICTLY A VIEW — the rota's truth is
 * OneTouch (local Encounter edits revert within the hour), so this renders
 * what the sync wrote and says so on-screen. Later, ADR-0040's shadow phase
 * gets to render proposed allocations as ghost chips on this same grid.
 *
 * Data: `_encounter` where subject.reference == the client's `_patient`
 * ref AND period.start within the week (the composite index
 * subject.reference ASC + period.start ASC already exists). Lazy: nothing
 * is fetched until the fold-out first opens; each week is fetched on
 * navigation and cached for the visit.
 *
 * Included shifts: any encounter with BOTH period.start and period.end
 * (synthetic demo rotas included — they carry no meta.source; ONE-004
 * anchor encounters carry no period and drop out) and status !=
 * entered-in-error. Week bucketing is UTC day-granular, matching the
 * sync's Z-suffixed timestamps.
 */

import {
  collection,
  doc,
  DocumentReference,
  getDoc,
  getDocs,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import { db } from "../firebase";
import {
  batchFetchPractitionerInfo,
  firstElementOfMaybeArray,
} from "../loaders/client-context";
import { escapeHtml } from "../utils/html";
import {
  blockGeometry,
  bucketByDay,
  DAY_MS,
  fmtTime,
  fmtWeekLabel,
  nowOffsetPct,
  weekDays,
  weekStartOf,
  WINDOW_END_MIN,
  WINDOW_START_MIN,
} from "./rota-week-core";
import type { RotaShift } from "./rota-week-core";

interface EncounterDoc {
  status?: string;
  period?: { start?: string; end?: string };
  participant?: Array<{ actor?: { reference?: DocumentReference; display?: string } }>;
}

async function resolvePatientRef(eocId: string): Promise<DocumentReference | null> {
  const eocSnap = await getDoc(doc(db, "_episodeofcare", eocId));
  const subj = firstElementOfMaybeArray<{ reference?: DocumentReference }>(
    eocSnap.data()?.subject);
  const ref = subj?.reference;
  return ref instanceof DocumentReference && ref.path.startsWith("_patient/") ? ref : null;
}

async function fetchWeek(patientRef: DocumentReference, days: string[]):
    Promise<Array<RotaShift & { display?: string }>> {
  const endExclusive = new Date(new Date(`${days[6]}T00:00:00Z`).getTime() + DAY_MS)
    .toISOString().slice(0, 10);
  const snap = await getDocs(query(
    collection(db, "_encounter"),
    where("subject.reference", "==", patientRef),
    where("period.start", ">=", `${days[0]}T00:00:00`),
    where("period.start", "<", `${endExclusive}T00:00:00`),
    orderBy("period.start", "asc"),
  ));
  const rows: Array<RotaShift & { display?: string }> = [];
  snap.forEach((d) => {
    const e = d.data() as EncounterDoc;
    const actor = e.participant?.[0]?.actor;
    rows.push({
      start: e.period?.start ?? "",
      end: e.period?.end ?? "",
      status: e.status,
      practitionerId: actor?.reference instanceof DocumentReference ? actor.reference.id : undefined,
      display: actor?.display,
    });
  });
  return rows;
}

/** Per-mount view state (a fresh detail render gets a fresh instance). */
interface RotaState {
  offset: number;
  patientRef: DocumentReference | null | undefined; // undefined = unresolved
  names: Map<string, string>;
}

async function renderWeek(fold: HTMLElement, eocId: string, st: RotaState): Promise<void> {
  const days = weekDays(weekStartOf(new Date(), st.offset));
  fold.innerHTML = `<div class="rota-loading">Loading rota…</div>`;

  if (st.patientRef === undefined) st.patientRef = await resolvePatientRef(eocId);
  if (!st.patientRef) {
    fold.innerHTML = `<div class="rota-empty">No patient record is linked to this client yet — no rota to show.</div>`;
    return;
  }

  let shifts: Array<RotaShift & { display?: string }>;
  try {
    shifts = await fetchWeek(st.patientRef, days);
  } catch (err) {
    console.warn("rota-week fetch:", err);
    fold.innerHTML = `<div class="rota-empty">Couldn't load the rota — try again in a moment.</div>`;
    return;
  }

  const unnamed = shifts.filter((s) => s.practitionerId && !s.display && !st.names.has(s.practitionerId!))
    .map((s) => s.practitionerId!) as string[];
  if (unnamed.length) {
    const info = await batchFetchPractitionerInfo(Array.from(new Set(unnamed)));
    for (const [id, pi] of info.entries()) st.names.set(id, pi.name);
  }

  const buckets = bucketByDay(shifts, days);
  const now = new Date();
  const todayIso = now.toISOString().slice(0, 10);
  const nowPct = nowOffsetPct(now);

  // Ruler ticks every 4 hours across the 06:00–22:00 window.
  const ticks: string[] = [];
  for (let m = WINDOW_START_MIN; m <= WINDOW_END_MIN; m += 240) {
    const pct = ((m - WINDOW_START_MIN) / (WINDOW_END_MIN - WINDOW_START_MIN)) * 100;
    ticks.push(`<span class="rota-tick" style="left:${pct}%">${String(m / 60).padStart(2, "0")}:00</span>`);
  }

  const rows = days.map((day) => {
    const label = new Date(`${day}T00:00:00Z`).toLocaleDateString("en-GB",
      { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" });
    const dayShifts = buckets.get(day) ?? [];
    const blocks = dayShifts.map((s) => {
      const geo = blockGeometry(day, s.start, s.end);
      if (!geo) return "";
      const who = (s as { display?: string }).display
        || (s.practitionerId ? st.names.get(s.practitionerId) : undefined)
        || "Carer";
      const times = `${fmtTime(s.start)}–${fmtTime(s.end)}`;
      // A narrow block can't hold text — the title carries it instead.
      const slim = geo.widthPct < 12;
      return `<span class="rota-block${geo.clamped ? " rota-clamped" : ""}"
        style="left:${geo.leftPct.toFixed(2)}%;width:${geo.widthPct.toFixed(2)}%"
        title="${escapeHtml(`${times} ${who}`)}">${
          slim ? "" : `<span class="rota-block-who">${escapeHtml(who)}</span><span class="rota-block-when">${times}</span>`
        }</span>`;
    }).join("");
    const nowLine = day === todayIso && nowPct !== null
      ? `<span class="rota-nowline" style="left:${nowPct.toFixed(2)}%"></span>` : "";
    return `<div class="rota-day${day === todayIso ? " rota-today" : ""}">
      <span class="rota-day-label">${escapeHtml(label)}</span>
      <span class="rota-track">${blocks || `<span class="rota-none">no shifts</span>`}${nowLine}</span>
    </div>`;
  }).join("");

  fold.innerHTML = `
    <div class="rota-head">
      <button class="rota-nav" data-rota-nav="-1" title="Previous week">‹</button>
      <span class="rota-label">${escapeHtml(fmtWeekLabel(days))}</span>
      <button class="rota-nav" data-rota-nav="1" title="Next week">›</button>
      ${st.offset !== 0 ? `<button class="rota-nav rota-now" data-rota-nav="0" title="Back to this week">This week</button>` : ""}
      <span class="rota-note">Rota from OneTouch — read-only</span>
    </div>
    <div class="rota-day rota-ruler-row" aria-hidden="true">
      <span class="rota-day-label"></span>
      <span class="rota-ruler">${ticks.join("")}</span>
    </div>
    <div class="rota-grid">${rows}</div>`;

  fold.querySelectorAll<HTMLElement>("[data-rota-nav]").forEach((btn) => {
    btn.addEventListener("click", () => {
      const nav = Number(btn.getAttribute("data-rota-nav"));
      st.offset = nav === 0 ? 0 : st.offset + nav;
      void renderWeek(fold, eocId, st);
    });
  });
}

/** Wire the hero calendar button to a lazily-rendered fold-out. */
export function wireRotaFoldout(btn: HTMLElement, fold: HTMLElement, eocId: string): void {
  const st: RotaState = { offset: 0, patientRef: undefined, names: new Map() };
  let rendered = false;
  btn.addEventListener("click", () => {
    const opening = fold.hidden;
    fold.hidden = !opening;
    btn.classList.toggle("ct-act-active", opening);
    if (opening && !rendered) {
      rendered = true;
      void renderWeek(fold, eocId, st);
    }
  });
}
